import { useGlobalSearchStore } from "client/Features/Layout/TitleBar/GlobalSearch/globalSearchStore";
import {
	isNilOrWhiteSpace
} from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ReadonlyRefObject,
	useEffect,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import {
	GlobalSearchCategoryResult,
	GlobalSearchCategoryResultStatus,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	GlobalSearchTermModel,
	ScopeSearchNodeModel,
	SearchRequest,
	registerSearchTerm,
	search,
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { ResultStatus } from "./ResultView/ResultView";
import { useSynchronizedRef } from "client/Hooks";
import { never } from "client_legacy/ndaw/errors";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { SearchNodeKind } from "client/Api/SearchSettings/searchSettings";
import { getMaxItemsPerScope } from "client/Features/Search/SearchResults/MultiScopeSearch/MultiScopeSearch";

const MAX_NUMBER_OF_SCOPES_IN_TOP_RESULT = 5;

let requestersIds: Guid[] = [];

export function useFetchGlobalSearchResults(
	allAvailableScopes: readonly ScopeSearchNodeModel[]
): void {
	const {
		searchTerm,
		selectedNode,
		searchCounter,
		recentSearchQueries,
		setResultState,
		startLoading,
		appendResult,
		finishLoading,
		setRecentSearchQueries
	} = useGlobalSearchStore(
		(x) => ({
			searchTerm: x.searchTerm,
			selectedNode: x.selectedNode,
			searchCounter: x.searchCounter,
			recentSearchQueries: x.recentSearchQueries,
			setResultState: x.setResultState,
			startLoading: x.startLoading,
			appendResult: x.appendResult,
			finishLoading: x.finishLoading,
			setSearchTerm: x.setSearchTerm,
			setRecentSearchQueries: x.setRecentSearchQueries
		}),
		shallowComparison
	);
	const [currentRequesterId] = useState<Guid>(() => newGuid());
	const recentSearchQueriesRef: ReadonlyRefObject<readonly GlobalSearchTermModel[]> = useSynchronizedRef(recentSearchQueries);

	useEffect(
		(): EffectWithDestructorResult => {
			requestersIds.push(currentRequesterId);

			return () => {
				requestersIds = requestersIds.filter(x => x !== currentRequesterId);
			};

		},
		[currentRequesterId]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			hasImplicitDependencyOn(searchCounter);

			async function fetchResult(
				signal: AbortSignal,
				onCancel: () => void
			): Promise<void> {
				if (isNilOrWhiteSpace(searchTerm)) {
					setResultState({
						status: ResultStatus.Success,
						results: [],
					});
					return;
				}

				startLoading();

				let numberOfRequestFailures: number = 0;
				let scopesToQuery: readonly ScopeSearchNodeModel[];

				if (selectedNode === null) {
					scopesToQuery = allAvailableScopes;
				} else {
					switch(selectedNode.kind) {
						case SearchNodeKind.Group:
							scopesToQuery = selectedNode.scopes;
							break;
						case SearchNodeKind.Scope:
							scopesToQuery = [selectedNode];
							break;
						default:
							never(selectedNode);
					}
				}

				const amountOfItems = getMaxItemsPerScope(scopesToQuery.length);
				const sortedScopesToFetch: readonly GlobalSearchScopeIdentifier[] = sortBy(
					scopesToQuery,
					x => x.usageSortOrder
				)
					.map(convertToIdentifier);

				const fetchForScope = async (scopeToQuery: GlobalSearchScopeIdentifier): Promise<GlobalSearchCategoryResult | null> => {
					try {
						let searchRequest: SearchRequest;

						switch (scopeToQuery.kind) {
							case GlobalSearchScopeKind.Predefined:
								searchRequest = {
									searchTerm,
									predefinedIntegrationKind: scopeToQuery.predefinedKind,
									maxItems: amountOfItems
								};
								break;
							case GlobalSearchScopeKind.IntegrationFlow:
								searchRequest = {
									searchTerm,
									customIntegrationId: scopeToQuery.integrationId,
									maxItems: amountOfItems
								};
								break;
							default:
								never(scopeToQuery);
						}

						const result: GlobalSearchCategoryResult | null = await search(
							searchRequest,
							signal
						);

						return result;

					} catch (e) {
						if (isCancelledError(e)) {
							return null;
						}
						numberOfRequestFailures++;
						return null;
					}
				};

				const promises: Promise<GlobalSearchCategoryResult | null>[] = sortedScopesToFetch.map(
					scope => fetchForScope(scope)
				);

				const fallBackPromises: Promise<GlobalSearchCategoryResult | null>[] = promises.splice(
					MAX_NUMBER_OF_SCOPES_IN_TOP_RESULT
				);

				const processPendingRequest = async (
					promise: Promise<GlobalSearchCategoryResult | null>
				): Promise<void> => {
					const result: GlobalSearchCategoryResult | null = await promise;

					if (signal.aborted) {
						return;
					}

					let wasAppended = false;
					if (result !== null) {
						switch (result.status) {
							case GlobalSearchCategoryResultStatus.Success:
								if (result.items.length > 0) {
									appendResult(result);
									wasAppended = true;
								}
								break;
							case GlobalSearchCategoryResultStatus.AuthenticationRequired:
								if (selectedNode !== null) {
									appendResult(result);
									wasAppended = true;
								}
								break;
							case GlobalSearchCategoryResultStatus.Failed:
								break;
							default:
								never(result);
						}
					}

					if (!wasAppended) {
						const nextToProcess: Promise<GlobalSearchCategoryResult | null> | undefined = fallBackPromises.shift();

						if (nextToProcess) {
							await processPendingRequest(nextToProcess);
						}
					}
				};

				await Promise.allSettled(promises.map(processPendingRequest));

				if (!signal.aborted) {
					finishLoading(numberOfRequestFailures < scopesToQuery.length);
					onCancel();
				}

				await Promise.allSettled(fallBackPromises);
			}

			async function registerSearchQuery(
				signal: AbortSignal
			): Promise<void> {
				try {
					if (searchTerm.trim()) {
						const newSearchTerm: GlobalSearchTermModel =
							await registerSearchTerm(searchTerm, signal);

						setRecentSearchQueries([
							...recentSearchQueriesRef.current
								.filter(x => x.id !== newSearchTerm.id),
							newSearchTerm
						]);
					}
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
				}
			}

			if (currentRequesterId !== requestersIds[0]) {
				return;
			}

			const cancellation = new AbortController();
			fetchResult(
				cancellation.signal,
				() => cancellation.abort()
			);

			registerSearchQuery(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			selectedNode,
			searchTerm,
			searchCounter,
			setResultState,
			startLoading,
			allAvailableScopes,
			finishLoading,
			appendResult,
			setRecentSearchQueries,
			recentSearchQueriesRef,
			currentRequesterId
		]
	);
}

export function convertToIdentifier(model: ScopeSearchNodeModel): GlobalSearchScopeIdentifier {
	switch (model.scopeKind) {
		case GlobalSearchScopeKind.Predefined:
			return {
				kind: GlobalSearchScopeKind.Predefined,
				predefinedKind: model.predefinedKind
			};
		case GlobalSearchScopeKind.IntegrationFlow:
			return {
				kind: GlobalSearchScopeKind.IntegrationFlow,
				integrationId: model.integrationId
			};
		default:
			never(model);
	}
}
